// @ts-nocheck
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { FacetModel, WarehouseModel } from "@insite/client-framework/Types/ApiModels";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox/Checkbox";
import CheckboxGroup, { CheckboxGroupProps } from "@insite/mobius/CheckboxGroup/CheckboxGroup";
import { Typography } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link/Link";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import constants from "@store/constants";
import { linkActionStyles } from "@store/Themes/common/mixins/link";
import { accordionFilterSectionProps } from "@store/Themes/common/props/accordionFilter";
import { StoreWarehouseModel } from "@store/Framework/Types/ApiModels";
import ga from "@toolkit/Framework/Common/GoogleAnalytics";
import textToSafeId from "@toolkit/Framework/Common/Utilities/textToSafeId";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { usePageLinkByPageType } from "@toolkit/Framework/Store/Links/LinksHooks";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import findIndex from "lodash/findIndex";
import React, { FC, useState } from "react";
import { css } from "styled-components";

const linkProps = {
    css: css`
        ${linkActionStyles()}
        ${spacingStyles(pxToRem(10), null)}
    `,
};

export interface ProductListStoreAvailabilityFilterAccordionSectionStyles {
    accordionSection?: AccordionSectionPresentationProps;
    checkBoxGroup?: CheckboxGroupProps;
    checkBox?: CheckboxPresentationProps;
    checkBoxSelected?: CheckboxPresentationProps;
    seeAllLink?: LinkPresentationProps;
    seeLessLink?: LinkPresentationProps;
    storeSelector?: InjectableCss;
    locatorLink?: LinkPresentationProps;
    distanceLabel?: TypographyPresentationProps;
    locatorTitle?: TypographyPresentationProps;
    locatorOtherStores?: InjectableCss;
}

export const productListStoreAvailabilityFilterAccordionSectionStyles: ProductListStoreAvailabilityFilterAccordionSectionStyles =
    {
        accordionSection: accordionFilterSectionProps,
        checkBoxGroup: {
            css: spacingStyles(pxToRem(10), null),
        },
        seeAllLink: linkProps,
        seeLessLink: linkProps,
        storeSelector: {
            css: css`
                position: relative;
                display: inline-block;
                padding: ${pxToRem(12)} ${pxToRem(14)};
                border: ${pxToRem(1)} solid ${getColor("common.border")};
                box-shadow: 0 ${pxToRem(1)} ${pxToRem(2)} rgba(0, 0, 0, 0.1);
                background-color: #fff;
                font-size: ${pxToRem(13)};
                text-align: left;
                text-transform: none;
                line-height: 1.4;
            `,
        },
        distanceLabel: {
            css: css`
                color: ${getColor("text.accent")};
                font-size: 0.85em;
                font-weight: ${x => x.theme.design.typography.weight.base};
                font-style: italic;
            `,
        },
        locatorTitle: {
            css: css`
                display: block;
                font-weight: ${x => x.theme.design.typography.weight.bold};
            `,
        },
        locatorOtherStores: {
            css: css`
                margin-top: ${pxToRem(10)};
                padding-top: ${pxToRem(10)};
                border-top: ${pxToRem(1)} solid ${getColor("common.border")};
            `,
        },
    };

export interface Props {
    title: string;
    facets: FacetModel[];
    warehouseAttributeFacets?: FacetModel[] | undefined;
    onChangeFacet: (facet: FacetModel) => void;
    showMoreLimit: number;
    extendedStyles?: ProductListStoreAvailabilityFilterAccordionSectionStyles;
    expandByDefault: boolean;
    pickUpWarehouse?: WarehouseModel;
    pickUpWarehouses?: StoreWarehouseModel[];
}

const ProductListStoreAvailabilityFiltersAccordionSection: FC<Props> = ({
    title,
    facets,
    warehouseAttributeFacets,
    onChangeFacet,
    showMoreLimit,
    expandByDefault,
    extendedStyles,
    pickUpWarehouse,
    pickUpWarehouses,
}) => {
    const trackForm = ga.use.trackEvent({ action: "gaEvent" });
    const locatorLink = usePageLinkByPageType(constants.pageType.locationFinder);
    const [styles] = useState(() =>
        mergeToNew(productListStoreAvailabilityFilterAccordionSectionStyles, extendedStyles),
    );
    const [expanded, setExpanded] = useState(false);
    const [accordionId] = useState(textToSafeId(title));

    const limitedFacets = expanded ? facets : facets?.slice(0, showMoreLimit);
    const anySelected = facets.find(f => f.selected) !== undefined;

    const selectedWarehouse = findIndex(warehouseAttributeFacets as any, { selected: true } as any);

    const [pickupOpen, setPickupOpen] = useState(false);

    const getDistanceFromLocationInMi = (lat1: number, lon1: number, lat2: number, lon2: number) => {
        const radius = 6371; // Radius of the earth in km
        const kmToMiles = 0.621371;
        const p = Math.PI / 180;
        const latitudeDegrees = Math.sin(((lat2 - lat1) * p) / 2);
        const longitudeDegrees = Math.sin(((lon2 - lon1) * p) / 2);

        const a =
            latitudeDegrees * latitudeDegrees +
            Math.cos(lat1 * p) * Math.cos(lat2 * p) * longitudeDegrees * longitudeDegrees;

        return (radius * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))) * kmToMiles).toFixed(2);
    };

    const getExtraData = (warehouseName: string) => {
        if (!pickUpWarehouses) {
            return;
        }
        const extraData = pickUpWarehouses.find(f => f.name === warehouseName);
        return extraData;
    };

    const getDistance = (warehouseName: string) => {
        if (!pickUpWarehouses || !pickUpWarehouse) {
            return;
        }
        const extraData = pickUpWarehouses.find(f => f.name === warehouseName);
        if (!extraData?.latitude || !extraData?.longitude) {
            return;
        }
        return getDistanceFromLocationInMi(
            pickUpWarehouse?.latitude,
            pickUpWarehouse?.longitude,
            extraData?.latitude,
            extraData?.longitude,
        );
    };

    const onChangePickupNow = (checkboxValue: boolean) => {
        const pickupWarehouseName = pickUpWarehouse?.name;
        const foundFacet = warehouseAttributeFacets?.find(f => {
            return f.name === pickupWarehouseName;
        });
        if (foundFacet) {
            trackForm({
                eventCategory: "Search",
                eventAction: "Filter",
                eventLabel: "Pickup Today",
            });
            onChangeFacet(foundFacet);
        }
        setPickupOpen(checkboxValue);
    };
    return (
        <ManagedAccordionSection
            title={title}
            uid={accordionId}
            {...styles.accordionSection}
            initialExpanded={expandByDefault || anySelected}
        >
            {limitedFacets?.map(facet => (
                <CheckboxGroup key={facet.id} {...styles.checkBoxGroup}>
                    <Checkbox
                        {...(facet.selected ? styles.checkBoxSelected : styles.checkBox)}
                        checked={facet.selected}
                        onChange={() => onChangeFacet(facet)}
                        data-test-selector={`facetFilter${facet.id}`}
                        uid={facet.id}
                    >
                        {facet.name} {facet.count !== -1 && `(${facet.count})`}
                    </Checkbox>
                </CheckboxGroup>
            ))}
            <CheckboxGroup {...styles.checkBoxGroup}>
                <Checkbox
                    checked={pickupOpen || selectedWarehouse > -1}
                    onChange={(e, value) => onChangePickupNow(value)}
                >
                    {translate("Pickup Now")}
                </Checkbox>
            </CheckboxGroup>

            {(pickupOpen || selectedWarehouse > -1) && (
                <StyledWrapper {...styles.storeSelector}>
                    <Typography>
                        <Typography {...styles.locatorTitle}>{translate("Your current store:")}</Typography>
                        {pickUpWarehouse?.city}, {pickUpWarehouse?.state}{" "}
                        <Link
                            {...styles.locatorLink}
                            onClick={() =>
                                trackForm({
                                    eventCategory: "Store",
                                    eventAction: "Initiate Change",
                                    eventLabel: "Change Link in Pickup Filter",
                                })
                            }
                            href={locatorLink?.url}
                        >
                            <Typography>| {translate("Change")}</Typography>
                        </Link>
                    </Typography>
                    <StyledWrapper {...styles.locatorOtherStores}>
                        <Typography {...styles.locatorTitle}>{translate("Other stores nearby:")}</Typography>
                        {warehouseAttributeFacets?.map(
                            facet =>
                                facet.name !== pickUpWarehouse?.name && (
                                    <CheckboxGroup key={facet.id} {...styles.checkBoxGroup}>
                                        <Checkbox
                                            {...(facet.selected ? styles.checkBoxSelected : styles.checkBox)}
                                            checked={facet.selected}
                                            onChange={() => onChangeFacet(facet)}
                                            data-test-selector={`facetFilter${facet.id}`}
                                            uid={facet.id}
                                        >
                                            {getExtraData(facet.name)?.city}, {getExtraData(facet.name)?.state}
                                            <Typography {...styles.distanceLabel}>
                                                {" "}
                                                ({getDistance(facet.name)} {translate("mi")})
                                            </Typography>
                                        </Checkbox>
                                    </CheckboxGroup>
                                ),
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            )}

            {facets &&
                facets.length > showMoreLimit &&
                (expanded ? (
                    <Link {...styles.seeLessLink} onClick={() => setExpanded(false)}>
                        {translate("See Less")}
                    </Link>
                ) : (
                    <Link {...styles.seeAllLink} onClick={() => setExpanded(true)}>
                        {translate("See All")}
                    </Link>
                ))}
        </ManagedAccordionSection>
    );
};

export default ProductListStoreAvailabilityFiltersAccordionSection;
